import React from "react";
import { Helmet } from "react-helmet-async";
import { useT } from "@/lib/i18n";
import seoRoutes from "@/lib/seoRoutes.json";
import {
  SITE,
  HERO_OG,
  buildLodgingSchema,
  buildWebSiteSchema,
  buildNearbySchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from "@/lib/seoSchemas";

/**
 * Head block for keyword landing pages.
 *
 * `routeId` selects the hreflang pair from the route registry; `page` is the
 * locale slice of landingContent.json. LodgingBusiness keeps the same @id as
 * the homepage so the knowledge graph dedupes the entity, while the FAQPage
 * here carries only this page's questions (deliberately disjoint from the
 * homepage FAQ so the two URLs never compete with identical markup).
 */
export const LandingSeo = ({ routeId, page }) => {
  const { t, lang } = useT();
  const route = seoRoutes.find((r) => r.id === routeId);
  const skUrl = `${SITE}${route.paths.sk}`;
  const enUrl = `${SITE}${route.paths.en}`;
  const canonical = lang === "en" ? enUrl : skUrl;
  const ogLocale = lang === "en" ? "en_US" : "sk_SK";

  const breadcrumbs = [
    { name: lang === "en" ? "Home" : "Úvod", item: lang === "en" ? `${SITE}/en` : `${SITE}/` },
    { name: page.hero.h1, item: canonical },
  ];

  const webPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonical}#webpage`,
    name: page.seo.title,
    description: page.seo.description,
    url: canonical,
    inLanguage: lang,
    isPartOf: { "@id": `${SITE}/#website` },
    about: { "@id": `${SITE}/#business` },
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{page.seo.title}</title>
      <meta name="description" content={page.seo.description} />
      <meta name="keywords" content={page.seo.keywords} />
      <link rel="canonical" href={canonical} />

      <link rel="alternate" hrefLang="sk" href={skUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={skUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content={t.site?.name ?? t.brand.name} />
      <meta property="og:title" content={page.seo.title} />
      <meta property="og:description" content={page.seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={HERO_OG} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.seo.title} />
      <meta name="twitter:description" content={page.seo.description} />
      <meta name="twitter:image" content={HERO_OG} />

      <script type="application/ld+json">{JSON.stringify(buildLodgingSchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(buildWebSiteSchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(webPage)}</script>
      <script type="application/ld+json">{JSON.stringify(buildNearbySchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(buildFaqSchema(page.faq.items))}</script>
      <script type="application/ld+json">{JSON.stringify(buildBreadcrumbSchema(breadcrumbs))}</script>
    </Helmet>
  );
};
